/* ==========================================================================
   estateService

   REAL MODE: reads the `estates` and `profiles` tables. getCurrentEstate()
   and listUsers() are both scoped to estateContext.activeEstateId, not
   sessionStore.user.estateId directly — for a regular user or
   estate_admin those are always the same thing, but for a super_admin
   who has switched to view a different estate, activeEstateId is what
   should actually be on screen.

   listEstates() and createEstate() are platform-level, like
   adminService — only a super_admin can call them for real. RLS
   ("estates_select_super_admin") and the create_estate() Postgres
   function both independently re-check that, same pattern as everything
   else privileged in this app.
   ========================================================================== */

import { config } from "../config";
import { getSupabase } from "./supabaseClient";
import { mapEstateRow } from "./supabaseMappers";
import { mockDb } from "./mock/mockDb";
import { delay } from "./mock/mockUtils";
import { sessionStore } from "../state/session";
import { requireActiveEstateId } from "../state/estateContext";
import type { AuthUser, CreateEstateRequest, Estate } from "../types";

async function getCurrentEstate(): Promise<Estate> {
  const estateId = requireActiveEstateId();

  if (config.useMockServices) {
    await delay(200);
    const estate = mockDb.getEstate(estateId);
    if (!estate) throw new Error("Estate not found.");
    return estate;
  }
  const { data, error } = await getSupabase().from("estates").select("*").eq("id", estateId).single();
  if (error || !data) throw new Error(error?.message ?? "Could not load estate.");
  return mapEstateRow(data);
}

/** Every estate a super_admin can see. Not scoped to activeEstateId (this
 *  is what populates the switcher itself, before an estate is picked). */
async function listEstates(): Promise<Estate[]> {
  if (config.useMockServices) {
    await delay(300);
    return mockDb.getEstates();
  }
  const { data, error } = await getSupabase().from("estates").select("*").order("name", { ascending: true });
  if (error) throw new Error(error.message);
  return (data ?? []).map(mapEstateRow);
}

async function createEstate(req: CreateEstateRequest): Promise<Estate> {
  if (!req.name.trim()) throw new Error("Enter an estate name.");

  if (config.useMockServices) {
    await delay(600);
    return mockDb.createEstate({ name: req.name.trim(), address: req.address?.trim() || undefined });
  }
  const { data, error } = await getSupabase().rpc("create_estate", {
    p_name: req.name.trim(),
    p_address: req.address?.trim() || null,
  });
  if (error) throw new Error(error.message);
  return mapEstateRow(data);
}

async function listUsers(): Promise<AuthUser[]> {
  const estateId = requireActiveEstateId();

  if (config.useMockServices) {
    await delay(400);
    const user = sessionStore.user;
    return user && user.estateId === estateId ? [user] : [];
  }
  const { data, error } = await getSupabase()
    .from("profiles")
    .select("id, name, email, phone, role, estate_id, created_at")
    .eq("estate_id", estateId);
  if (error) throw new Error(error.message);
  return (data ?? []).map((row) => ({
    id: row.id,
    name: row.name,
    email: row.email,
    phone: row.phone ?? undefined,
    role: row.role,
    estateId: row.estate_id,
    createdAt: row.created_at,
  }));
}

export const estateService = { getCurrentEstate, listEstates, createEstate, listUsers };
